import React, { useState } from "react";
import {
  Box,
  Button,
  Divider,
  Grid,
  Typography,
  InputAdornment,
  IconButton,
  TextField,
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlined from '@mui/icons-material/VisibilityOffOutlined';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { object, string } from 'zod';
import PasswordStrengthBar from 'react-password-strength-bar';
import { useNavigate } from "react-router-dom";
import ConfirmModal from '../../../components/layout/ConfirmModal';
import FormInput from "../../../components/form/FormInput";
import api from "../../../utils/api";

const passwordSchema = object({
  password: string()
    .min(1, 'Password is required')
    .min(8, 'Password must be more than 8 characters')
    .max(32, 'Password must be less than 32 characters'),
  passwordConfirm: string().min(1, 'Please confirm your password'),
}).refine((data) => data.password === data.passwordConfirm, {
  path: ['passwordConfirm'],
  message: 'Passwords do not match',
});

export default function MasterPassword({ userId }) {
  const theme = useTheme();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const defaultValues = {
    password: '',
    passwordConfirm: '',
  };

  const methods = useForm({
    resolver: zodResolver(passwordSchema),
    defaultValues,
  });

  const { register, formState: { errors } } = methods;

  const onSubmitHandler = async (data) => {
    const res = await api.post(`/users/update-master-key`, {
      password: data.password,
    });
    if (res.data?.success) {
      setShowConfirmModal(true);
    }
  };

  const onCloseConfirmModal = () => setShowConfirmModal(false);
  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const watchPassword = methods.watch("password", "");

  return (
    <>
      <Grid>
        <Typography sx={{ fontWeight: 600, fontSize: '1.5rem' }}>Master Password Settings</Typography>
      </Grid>
      <Divider sx={{ mt: 2 }} />
      <FormProvider {...methods}>
        <Box
          display='flex'
          flexDirection='column'
          component='form'
          noValidate
          autoComplete='off'
          onSubmit={methods.handleSubmit(onSubmitHandler)}>
          <Grid container sx={{ mt: 5 }}>
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontWeight: 600, fontSize: '18px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>New Password</Typography>
              <Typography sx={{ fontWeight: 500, fontSize: '14px', color: theme.palette.mode === 'light' ? '#7e8299' : '#6d6d80' }}>Change your existing password.</Typography>
            </Grid>
            <Grid item xs={12} md={6} sx={{ mt: { xs: 1, md: 0 } }}>
              <TextField
                fullWidth
                variant="standard"
                type={showPassword ? "text" : "password"}
                placeholder='Password'
                name='password'
                required
                error={!!errors['password']}
                helperText={errors['password'] ? (errors['password'].message) : ''}
                {...register('password')}
                InputProps={{
                  style: {
                    fontWeight: 600,
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleClickShowPassword}
                      >
                        {showPassword ?
                          <VisibilityOutlinedIcon sx={{ color: theme.palette.mode === 'light' ? '#a1a5b7' : '#565674' }} /> :
                          <VisibilityOffOutlined sx={{ color: theme.palette.mode === 'light' ? '#a1a5b7' : '#565674' }} />
                        }
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 2 }}
              />
              <PasswordStrengthBar
                password={watchPassword}
                shortScoreWord='Use 8 or more characters with a mix of letters, numbers & symbols.'
                style={{ marginTop: '5px', marginBottom: '8px' }}
              />
            </Grid>
          </Grid>
          <Grid container sx={{ mt: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontWeight: 600, fontSize: '18px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>Repeat Password</Typography>
              <Typography sx={{ fontWeight: 500, fontSize: '14px', color: theme.palette.mode === 'light' ? '#7e8299' : '#6d6d80' }}>Confirm new password by typing it again.</Typography>
            </Grid>
            <Grid item xs={12} md={6} sx={{ mt: { xs: 1, md: 0 } }}>
              <FormInput
                fullWidth
                type='password'
                placeholder='Repeat Password'
                name='passwordConfirm'
                required
              />
            </Grid>
          </Grid>
          <Divider sx={{ mt: 5 }} />
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button
              onClick={() => navigate('/superadmin')}
              size="large"
              sx={{
                backgroundColor: theme.palette.mode === 'light' ? '#f5f8fa' : '#2b2b40',
                color: theme.palette.mode === 'light' ? '#7e8299' : '#6d6d80',
                fontWeight: 500,
                textTransform: 'none',
                borderRadius: '6px',
                padding: '11px 20px',
                "&:hover": {
                  backgroundColor: theme.palette.mode === 'light' ? '#f1faff' : '#212E48',
                  color: '#009ef7',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              size="large"
              sx={{ marginInlineStart: 1, color: '#fff', fontWeight: 600, textTransform: 'none' }}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      </FormProvider>
      <ConfirmModal
        visible={showConfirmModal}
        onClose={onCloseConfirmModal}
        symbol="&#10004;"
        title="Success!"
        description="You have successfully updated master password."
        titleNo="Continue"
        onClickNo={onCloseConfirmModal}
      />
    </>
  );
}
